import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserCheck,
  UserX,
  Phone,
  Store,
  MapPin,
  Search,
  Clock,
  ShieldQuestion,
  UserCog,
  Loader2,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";
import { smartMatch } from "../../utils/arabicSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "الآن";
    if (mins < 60) return `قبل ${mins} دقيقة`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `قبل ${hrs} ساعة`;
    const days = Math.floor(hrs / 24);
    return `قبل ${days} يوم`;
  } catch {
    return "—";
  }
};

const PendingCustomersTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pending-customers`);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("فشل تحميل قائمة الزبائن المعلّقين");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 15000); // مزامنة كل 15 ثانية
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim();
    return rows.filter((r) => {
      const hay = [
        r.full_name,
        r.market_name,
        r.phone,
        r.created_by_supervisor_name,
        r.customer_id,
      ].filter(Boolean).join(" ");
      return smartMatch(hay, q);
    });
  }, [rows, search]);

  const handleApprove = async (row) => {
    const ok = await confirmDialog(
      `الموافقة على الزبون «${row.full_name || row.phone}» الذي أضافه المشرف «${row.created_by_supervisor_name || "—"}»؟\n\nبعد الموافقة سيظهر في القوائم الرئيسية وسيدخل ضمن حساب المكافآت والتصنيف.`
    );
    if (!ok) return;
    setBusyId(row.id);
    try {
      await axios.patch(`${API}/admin/pending-customers/${row.id}/approve`);
      toast.success("✅ تمت الموافقة على الزبون");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشلت الموافقة");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (row) => {
    const ok = await confirmDialog(
      `⚠️ رفض الزبون «${row.full_name || row.phone}»؟\n\nسيتم حذف حساب الزبون وجميع فواتيره المعلّقة (${row.pending_order_count || 0}) بشكل نهائي.\nهذا الإجراء لا يمكن التراجع عنه.`
    );
    if (!ok) return;
    setBusyId(row.id);
    try {
      const { data } = await axios.patch(`${API}/admin/pending-customers/${row.id}/reject`);
      toast.success(`تم رفض الزبون وحذف ${data.orders_deleted || 0} فاتورة`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الرفض");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="pending-customers-tab"
    >
      {/* Header banner */}
      <div className="rounded-2xl border-2 border-orange-300 bg-gradient-to-l from-orange-50 to-amber-50 p-4 flex items-start gap-3 shadow-sm">
        <div className="bg-orange-500 text-white rounded-xl p-2.5 shrink-0">
          <ShieldQuestion className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-orange-900 mb-1">
            الزبائن قيد موافقة الأدمن
          </h2>
          <p className="text-sm text-orange-800 leading-relaxed">
            هؤلاء الزبائن أضافهم <b>المشرفون الميدانيون</b> أثناء البيع.
            لن يظهروا في القوائم الرئيسية ولن تُحتسب فواتيرهم بالمكافآت
            حتى تُوافق عليهم من هنا.
          </p>
        </div>
        <div
          className="bg-orange-500 text-white font-bold rounded-full px-3 py-1 text-sm shrink-0"
          data-testid="pending-customers-count"
        >
          {rows.length}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 بحث بالاسم / الماركت / الهاتف / اسم المشرف..."
          className="w-full border-2 border-gray-200 rounded-xl pr-10 pl-4 py-3 focus:outline-none focus:border-orange-400 bg-white text-right"
          data-testid="pending-customers-search"
        />
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>جارٍ التحميل...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
          <UserCheck className="w-16 h-16 mx-auto text-green-400 mb-3" />
          <p className="text-lg font-bold text-gray-700">
            {rows.length === 0 ? "لا يوجد زبائن بانتظار الموافقة" : "لا توجد نتائج للبحث"}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {rows.length === 0
              ? "أي زبون يضيفه المشرف الميداني سيظهر هنا للموافقة قبل تفعيله."
              : "جرّب كلمة بحث مختلفة."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((r) => (
              <motion.div
                key={r.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9, x: -20 }}
                className="bg-white rounded-2xl border-2 border-orange-200 p-4 shadow-sm hover:shadow-md hover:border-orange-400 transition-all"
                data-testid={`pending-customer-card-${r.id}`}
              >
                {/* Top row: name + tier */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Store className="w-4 h-4 text-orange-500 shrink-0" />
                      <span
                        className="font-bold text-gray-900 truncate"
                        data-testid={`pending-customer-market-${r.id}`}
                      >
                        {r.market_name || "— لا يوجد اسم ماركت —"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 truncate">
                      👤 {r.full_name || "—"}
                    </div>
                  </div>
                  <span className="text-[11px] bg-orange-100 text-orange-700 rounded-full px-2 py-0.5 font-bold shrink-0">
                    {r.customer_tier?.replace("_plus", "+") || "A+"}
                  </span>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-blue-500" />
                    <span dir="ltr">{r.phone || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-gray-400" />
                    <span>{timeAgo(r.created_at)}</span>
                  </div>
                  {r.location && (
                    <div className="flex items-center gap-1.5 col-span-2 truncate">
                      <MapPin className="w-3.5 h-3.5 text-red-500" />
                      <span className="truncate">{r.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 col-span-2 bg-purple-50 rounded-lg px-2 py-1.5">
                    <UserCog className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                    <span className="text-purple-800 font-medium truncate">
                      المشرف: {r.created_by_supervisor_name || "—"}
                    </span>
                  </div>
                  {(r.order_count > 0) && (
                    <div className="flex items-center gap-1.5 col-span-2 bg-amber-50 rounded-lg px-2 py-1.5">
                      <FileText className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span className="text-amber-800 font-medium">
                        عدد الفواتير: {r.order_count}
                        {r.pending_order_count > 0 && (
                          <> — منها {r.pending_order_count} معلّقة</>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleApprove(r)}
                    disabled={busyId === r.id}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-bold rounded-xl py-2 flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`approve-btn-${r.id}`}
                  >
                    {busyId === r.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <UserCheck className="w-4 h-4" />
                    )}
                    موافقة
                  </button>
                  <button
                    onClick={() => handleReject(r)}
                    disabled={busyId === r.id}
                    className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white font-bold rounded-xl py-2 flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`reject-btn-${r.id}`}
                  >
                    <UserX className="w-4 h-4" />
                    رفض
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default PendingCustomersTab;
